import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ClipboardList,
  PhoneCall,
  Mail,
  Video,
  Calendar,
  FileText,
  Plus,
  Search,
  CheckCircle2,
  Clock,
  Building2,
  Download,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { trpc } from "@/lib/trpc";

// ─── Types ───────────────────────────────────────────────────────────────────

type ActivityType = "CALL" | "EMAIL" | "DEMO" | "MEETING" | "NOTE";

// ─── Config ───────────────────────────────────────────────────────────────────

const ACTIVITY_CONFIG: Record<ActivityType, { label: string; icon: any; color: string; bgColor: string }> = {
  CALL: { label: "Appel", icon: PhoneCall, color: "text-primary", bgColor: "bg-primary/5" },
  EMAIL: { label: "Email", icon: Mail, color: "text-pipeline-demo", bgColor: "bg-pipeline-demo/10" },
  DEMO: { label: "Démo", icon: Video, color: "text-success", bgColor: "bg-success/10" },
  MEETING: { label: "RDV", icon: Calendar, color: "text-warning", bgColor: "bg-warning/10" },
  NOTE: { label: "Note", icon: FileText, color: "text-muted-foreground", bgColor: "bg-muted" },
};

// ─── Upcoming Activity Row ────────────────────────────────────────────────────

interface UpcomingActivity {
  id: string;
  type: string;
  notes: string;
  leadId: string;
  leadName: string;
  leadCompany: string;
  author: string;
  status: string;
  createdAt: string;
  scheduledAt?: string;
  dueDate?: string;
}

function UpcomingActivityRow({
  act,
  canAct,
  onMarkDone,
  isMarking,
}: {
  act: UpcomingActivity;
  canAct: boolean;
  onMarkDone: (id: string) => void;
  isMarking: boolean;
}) {
  const cfg = ACTIVITY_CONFIG[act.type as ActivityType];
  if (!cfg) return null;
  const Icon = cfg.icon;

  const isLate =
    act.scheduledAt != null && new Date(act.scheduledAt) < new Date();

  return (
    <div className="flex gap-3">
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${cfg.bgColor} ${cfg.color}`}
      >
        <Icon aria-hidden="true" className="h-4 w-4" />
      </div>
      <Card className="flex-1">
        <CardContent className="py-3 px-4">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold ${cfg.color}`}>
                {cfg.label}
              </span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">
                <Building2 aria-hidden="true" className="inline h-3 w-3 mr-0.5" />
                {act.leadName} ({act.leadCompany})
              </span>
              {isLate && (
                <Badge
                  variant="destructive"
                  className="text-[10px] px-1.5 py-0 h-4 gap-0.5"
                >
                  <AlertCircle aria-hidden="true" className="h-2.5 w-2.5" />
                  En retard
                </Badge>
              )}
            </div>
            {canAct && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs shrink-0"
                onClick={() => onMarkDone(act.id)}
                disabled={isMarking}
                aria-label={`Marquer l'activité de ${act.leadName} comme faite`}
              >
                <CheckCircle2 aria-hidden="true" className="h-3 w-3 mr-1" />
                Marquer fait
              </Button>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{act.notes}</p>
          {act.scheduledAt && (
            <div
              className={`flex items-center gap-1 mt-1.5 text-xs ${
                isLate ? "text-destructive" : "text-warning"
              }`}
            >
              <Clock aria-hidden="true" className="h-3 w-3" />
              Planifié:{" "}
              {new Date(act.scheduledAt).toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-1">{act.author}</div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SalesActivities() {
  const { user } = useAuth();
  const canAdd = canWrite(user?.staffRole);

  const { data: activitiesData, isLoading, refetch } = trpc.sales.leads.getActivities.useQuery({ limit: 100 });
  const { data: upcomingData, isLoading: isLoadingUpcoming, refetch: refetchUpcoming } =
    trpc.sales.leads.getUpcomingActivities.useQuery({ limit: 100 });
  const { data: leadsData } = trpc.sales.leads.list.useQuery();

  const addActivityMutation = trpc.sales.leads.addActivity.useMutation({
    onSuccess: () => {
      toast.success("Activité enregistrée");
      setShowDialog(false);
      setForm({ type: "NOTE", notes: "", leadId: "", scheduledAt: "" });
      refetch();
      refetchUpcoming();
    },
    onError: () => toast.error("Erreur lors de l'enregistrement"),
  });

  const markDoneMutation = trpc.sales.leads.markActivityDone.useMutation({
    onSuccess: () => {
      toast.success("Activité marquée comme faite");
      refetch();
      refetchUpcoming();
    },
    onError: () => toast.error("Erreur lors de la mise à jour"),
  });

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<ActivityType | "ALL">("ALL");
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState<{
    type: ActivityType;
    notes: string;
    leadId: string;
    scheduledAt: string;
  }>({ type: "NOTE", notes: "", leadId: "", scheduledAt: "" });

  const activities = activitiesData ?? [];
  const upcoming = upcomingData ?? [];
  const leads = leadsData ?? [];

  // Set default leadId once leads are loaded
  const defaultLeadId = leads[0]?.id ?? "";

  const filtered = activities
    .filter((a) => {
      const matchSearch =
        !search ||
        a.leadName.toLowerCase().includes(search.toLowerCase()) ||
        a.leadCompany.toLowerCase().includes(search.toLowerCase()) ||
        a.notes.toLowerCase().includes(search.toLowerCase());
      const matchType = typeFilter === "ALL" || a.type === typeFilter;
      return matchSearch && matchType;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Group history by date
  const grouped: Record<string, typeof filtered> = {};
  for (const act of filtered) {
    const date = new Date(act.createdAt).toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(act);
  }

  // Upcoming count for badge
  const upcomingCount = upcoming.length;

  function handleOpenDialog() {
    setForm((f) => ({ ...f, leadId: f.leadId || defaultLeadId }));
    setShowDialog(true);
  }

  function handleCreate() {
    if (!form.notes) {
      toast.error("Notes requises");
      return;
    }
    const leadId = form.leadId || defaultLeadId;
    if (!leadId) {
      toast.error("Veuillez sélectionner un lead");
      return;
    }
    addActivityMutation.mutate({
      leadId,
      type: form.type,
      notes: form.notes,
      scheduledAt: form.scheduledAt || undefined,
    });
  }

  function handleExportCSV() {
    const header = "Date,Type,Lead,Entreprise,Auteur,Notes\n";
    const rows = filtered
      .map(
        (a) =>
          `"${new Date(a.createdAt).toLocaleDateString("fr-FR")}","${ACTIVITY_CONFIG[a.type as ActivityType]?.label ?? a.type}","${a.leadName}","${a.leadCompany}","${a.author}","${a.notes.replace(/"/g, "''")}"`
      )
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activites_sales_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV téléchargé");
  }

  // Activity type stats (based on full history)
  const typeCounts = Object.keys(ACTIVITY_CONFIG).map((t) => ({
    type: t as ActivityType,
    count: activities.filter((a) => a.type === t).length,
  }));

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList aria-hidden="true" className="h-6 w-6 text-primary" />
            Journal des activités
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {activities.length} activité{activities.length > 1 ? "s" : ""} enregistrée{activities.length > 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV} aria-label="Exporter les activités en CSV">
            <Download aria-hidden="true" className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          {canAdd && (
            <Button onClick={handleOpenDialog}>
              <Plus aria-hidden="true" className="h-4 w-4 mr-2" />
              Nouvelle activité
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="todo">
        <TabsList className="mb-5">
          <TabsTrigger value="todo" className="flex items-center gap-2">
            À faire
            {upcomingCount > 0 && (
              <Badge
                variant={upcoming.some((a) => a.scheduledAt && new Date(a.scheduledAt) < new Date()) ? "destructive" : "secondary"}
                className="text-[10px] px-1.5 py-0 h-4 min-w-[1.25rem] flex items-center justify-center"
              >
                {upcomingCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history">Historique</TabsTrigger>
        </TabsList>

        {/* ── À faire tab ─────────────────────────────────────────────────── */}
        <TabsContent value="todo">
          {isLoadingUpcoming && (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              Chargement…
            </div>
          )}

          {!isLoadingUpcoming && upcoming.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground text-sm">
                Aucune activité planifiée
              </CardContent>
            </Card>
          )}

          {!isLoadingUpcoming && upcoming.length > 0 && (
            <div className="space-y-3">
              {upcoming.map((act) => (
                <UpcomingActivityRow
                  key={act.id}
                  act={act}
                  canAct={canAdd}
                  onMarkDone={(id) => markDoneMutation.mutate({ id })}
                  isMarking={markDoneMutation.isPending}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Historique tab ───────────────────────────────────────────────── */}
        <TabsContent value="history">
          {/* Stats pills */}
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            {typeCounts.map(({ type, count }) => {
              const cfg = ACTIVITY_CONFIG[type];
              const Icon = cfg.icon;
              return (
                <button
                  key={type}
                  onClick={() => setTypeFilter(typeFilter === type ? "ALL" : type)}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                    typeFilter === type
                      ? `${cfg.bgColor} ${cfg.color} border-current`
                      : "bg-muted text-muted-foreground border-transparent hover:border-muted-foreground/30"
                  }`}
                  aria-pressed={typeFilter === type}
                  aria-label={`Filtrer par type : ${cfg.label}`}
                >
                  <Icon aria-hidden="true" className="h-3 w-3" />
                  {cfg.label}
                  <span className="bg-white/70 rounded-full px-1.5">{count}</span>
                </button>
              );
            })}
            {typeFilter !== "ALL" && (
              <button
                onClick={() => setTypeFilter("ALL")}
                className="text-xs text-muted-foreground underline"
              >
                Tout afficher
              </button>
            )}
          </div>

          {/* Search */}
          <div className="relative mb-4 max-w-sm">
            <Search aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              aria-label="Rechercher une activité"
              placeholder="Rechercher dans les activités..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              Chargement…
            </div>
          )}

          {!isLoading && Object.keys(grouped).length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground text-sm">
                Aucune activité trouvée
              </CardContent>
            </Card>
          )}

          {Object.entries(grouped).map(([date, acts]) => (
            <div key={date} className="mb-6">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                {date}
              </div>
              <div className="space-y-3">
                {acts.map((act) => {
                  const cfg = ACTIVITY_CONFIG[act.type as ActivityType];
                  if (!cfg) return null;
                  const Icon = cfg.icon;
                  return (
                    <div key={act.id} className="flex gap-3">
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${cfg.bgColor} ${cfg.color}`}
                      >
                        <Icon aria-hidden="true" className="h-4 w-4" />
                      </div>
                      <Card className="flex-1">
                        <CardContent className="py-3 px-4">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div>
                              <span className={`text-xs font-semibold ${cfg.color}`}>
                                {cfg.label}
                              </span>
                              <span className="text-xs text-muted-foreground mx-2">·</span>
                              <span className="text-xs text-muted-foreground">
                                <Building2 aria-hidden="true" className="inline h-3 w-3 mr-0.5" />
                                {act.leadName} ({act.leadCompany})
                              </span>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-xs text-muted-foreground">
                                {new Date(act.createdAt).toLocaleTimeString("fr-FR", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                              <div className="text-xs text-muted-foreground">{act.author}</div>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">{act.notes}</p>
                          {act.scheduledAt && (
                            <div className="flex items-center gap-1 mt-1.5 text-xs text-warning">
                              <Clock aria-hidden="true" className="h-3 w-3" />
                              Planifié:{" "}
                              {new Date(act.scheduledAt).toLocaleDateString("fr-FR", {
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </div>
                          )}
                          {act.completedAt && (
                            <div className="flex items-center gap-1 mt-1.5 text-xs text-success">
                              <CheckCircle2 aria-hidden="true" className="h-3 w-3" />
                              Complété
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      {/* Create dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle activité</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Lead *</Label>
              <Select
                value={form.leadId || defaultLeadId}
                onValueChange={(v) => setForm((f) => ({ ...f, leadId: v }))}
              >
                <SelectTrigger><SelectValue placeholder="Sélectionner un lead" /></SelectTrigger>
                <SelectContent>
                  {leads.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name} — {l.company}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Type *</Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm((f) => ({ ...f, type: v as ActivityType }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ACTIVITY_CONFIG).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes *</Label>
              <Textarea
                placeholder="Résumé de l'interaction..."
                rows={4}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Date planifiée</Label>
              <Input
                type="datetime-local"
                value={form.scheduledAt}
                onChange={(e) => setForm((f) => ({ ...f, scheduledAt: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={addActivityMutation.isPending}>
              {addActivityMutation.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
